import React, { useEffect, useState, useContext } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Box,
  TextField,
  Pagination,
  MenuItem,
  Chip,
} from "@mui/material";
import { Link } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import StarIcon from "@mui/icons-material/Star";
import axios from "../services/axiosInstance";
import { UserContext } from "../context/UserContext";

const Jobs = () => {
  const { user } = useContext(UserContext);

  const [allJobs, setAllJobs] = useState([]);
  const [locations, setLocations] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const jobsPerPage = 6;

  const [search, setSearch] = useState("");
  const [jobType, setJobType] = useState("");
  const [salaryRange, setSalaryRange] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedLocation, setSelectedLocation] = useState("");

  const jobTypes = ["Full-time", "Part-time", "Remote", "Internship"];
  const salaryRanges = ["0-30000", "30000-50000", "50000-80000", ">80000"];
  const sortOptions = ["Newest", "Highest Salary"];
  const API_URL = window.__ENV__?.API_URL || "https://api.cityzoom.in/api";

  useEffect(() => {
    fetchJobsFromAPI();
  }, [search, jobType, salaryRange, sortBy]);

  useEffect(() => {
    setPage(1);
  }, [selectedLocation]);

  const fetchJobsFromAPI = async () => {
    setLoading(true);
    try {
      // ✅ Fix: send raw string values directly
      const response = await axios.get(`${API_URL}/jobs`, {
        params: {
          page: 1,
          limit: 1000,
          search,
          jobType,
          salaryRange: salaryRange || undefined,
          sortBy: sortBy || "newest",
        },
      });

      const fetchedJobs = response.data.jobs || [];
      setAllJobs(fetchedJobs);

      const uniqueLocations = [
        ...new Set(fetchedJobs.map((job) => job.location?.trim()).filter(Boolean)),
      ];
      setLocations(uniqueLocations);
      setError(null);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError("Failed to load jobs. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = selectedLocation
    ? allJobs.filter((job) => job.location === selectedLocation)
    : allJobs;

  const paginatedJobs = filteredJobs.slice(
    (page - 1) * jobsPerPage,
    page * jobsPerPage
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 4 }}>
      <Typography
        variant="h4"
        color="#42326e"
        textAlign="center"
        fontWeight="bold"
        mb={3}
      >
        Job Listings
      </Typography>

      {/* 🔍 Filters */}
      <Box sx={{ display: "flex", gap: 5, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="Search Jobs..."
          variant="outlined"
          size="medium"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 250 }}
        />
        <TextField
          select
          label="Job Type"
          variant="outlined"
          size="medium"
          value={jobType}
          onChange={(e) => setJobType(e.target.value)}
          sx={{ width: 250 }}
        >
          {jobTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Salary Range"
          variant="outlined"
          size="medium"
          value={salaryRange}
          onChange={(e) => setSalaryRange(e.target.value)}
          sx={{ width: 250 }}
        >
          {salaryRanges.map((range) => (
            <MenuItem key={range} value={range}>
              {range}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Sort By"
          variant="outlined"
          size="medium"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          sx={{ width: 250 }}
        >
          {sortOptions.map((option) => (
            <MenuItem key={option} value={option.toLowerCase()}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Location"
          variant="outlined"
          size="medium"
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
          sx={{ width: 250 }}
        >
          <MenuItem value="">All Locations</MenuItem>
          {locations.map((loc) => (
            <MenuItem key={loc} value={loc}>
              {loc}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {paginatedJobs.length > 0 ? (
              paginatedJobs.map((job) => (
                <Grid item xs={12} sm={6} md={4} key={job.id}>
                  <Card
                    sx={{
                      minHeight: 200,
                      p: 2,
                      boxShadow: 3,
                      position: "relative",
                      bgcolor: job.premium
                        ? "rgba(255, 223, 186, 0.3)"
                        : "white",
                    }}
                  >
                    <CardContent>
                      {job.premium && (
                        <Chip
                          label="Premium"
                          color="secondary"
                          icon={<StarIcon />}
                          sx={{ position: "absolute", top: 10, right: 10 }}
                        />
                      )}
                      <Typography variant="h6" fontWeight="bold">
                        {job.title}
                      </Typography>

                      <Box display="flex" alignItems="center" mt={1}>
                        <BusinessIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">{job.company}</Typography>
                      </Box>

                      <Box display="flex" alignItems="center" mt={1}>
                        <LocationOnIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">{job.location}</Typography>
                      </Box>

                      <Box display="flex" alignItems="center" mt={1}>
                        <CurrencyRupeeIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">{job.salary}</Typography>
                      </Box>

                      <Box display="flex" alignItems="center" mt={1}>
                        <AssignmentIndIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">{job.job_type}</Typography>
                      </Box>

                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          mt: 2,
                          width: "100%",
                          backgroundColor: "#42326e",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "black",
                          },
                        }}
                        component={Link}
                        to={`/job/${job.id}`}
                        disabled={!user}
                      >
                        {user ? "View Details" : "Please Login to View"}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Typography
                variant="body1"
                textAlign="center"
                sx={{ width: "100%", mt: 3 }}
              >
                No jobs available at the moment.
              </Typography>
            )}
          </Grid>

          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={Math.ceil(filteredJobs.length / jobsPerPage)}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
            />
          </Box>
        </>
      )}
    </Container>
  );
};

export default Jobs;
